import React, {useRef, useCallback} from 'react';
import clsx from 'clsx';

import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	getArrowFocusTrapItemUtilityClass,
	arrowFocusTrapItemClasses,
	ArrowFocusTrapItemClasses,
	ARROW_FOCUS_TRAP_ITEM_CLASS_NAME,
} from './arrowFocusTrapItemClasses';
import {useArrowFocusTrapItem} from '../../hooks/useArrowFocusTrapItem';

export type ArrowFocusTrapItemElement = HTMLElement;
export type ArrowFocusTrapItemRef =
	| React.RefObject<ArrowFocusTrapItemElement>
	| ((element: HTMLElement | null) => void)
	| null;

type ArrowFocusTrapItemUtiltiyClassProps = WithClasses<
	ArrowFocusTrapItemClasses,
	{
		focusActive?: boolean;
		focusDisabled?: boolean;
	}
>;

const useUtilityClasses = (ownerState: ArrowFocusTrapItemUtiltiyClassProps) => {
	const {classes, focusActive, focusDisabled} = ownerState;
	const slots = {
		root: [
			'root',
			focusActive && 'focusActive',
			focusDisabled && 'focusDisabled',
		],
	};
	return composeClasses(slots, getArrowFocusTrapItemUtilityClass, classes);
};

const ArrowFocusTrapItemRoot = styled('div', {
	name: ARROW_FOCUS_TRAP_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	[`&.${arrowFocusTrapItemClasses.focusActive}:not(.Mui-selected):not(:hover)`]:
		{
			backgroundColor: theme.palette.custom.background.light,
		},
}));

type TopLevelComponenetProps<
	C extends HTMLDivElement | HTMLLIElement | React.ElementType = HTMLDivElement,
> = C extends HTMLDivElement | HTMLLIElement
	? React.DetailedHTMLProps<React.HTMLAttributes<C>, C> // TODO: the typing is working from the outside, just need to dive a bit deeper and see what i am missing here that is // making ts unhappy but working...
	: JSX.IntrinsicAttributes &
			// @ts-ignore
			JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>;

// TODO: props for the component or for the element
export type ArrowFocusTrapItemProps<
	C extends HTMLDivElement | HTMLLIElement | React.ElementType = HTMLDivElement,
> = WithClasses<
	ArrowFocusTrapItemClasses,
	React.PropsWithChildren<
		TopLevelComponenetProps<C> & {
			ref?: ArrowFocusTrapItemRef;
			index: number;
			onClick?: (
				event: React.MouseEvent<HTMLElement, MouseEvent>,
				index: number,
			) => void;
			ContainerComponent?: React.ElementType<
				React.HTMLAttributes<HTMLDivElement | HTMLLIElement>
			>;
		}
	>
>;

export const ArrowFocusTrapItemPrimitive = <
	C extends HTMLDivElement | HTMLLIElement | React.ElementType = HTMLDivElement,
>(
	{
		classes: _classes,
		className,
		children: childrenProp,
		index,
		onClick,
		ContainerComponent = 'div',
		...props
	}: ArrowFocusTrapItemProps<C>,
	ref: ArrowFocusTrapItemRef,
) => {
	const rootRef = useSharedRef<HTMLElement | null>(null, [ref]);

	const {isActive, attributes, handleItemClick, handleItemInteractStart} =
		useArrowFocusTrapItem({index, ref: rootRef});

	const classes = useUtilityClasses({
		classes: _classes,
		focusActive: isActive,
	});

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
			onClick?.(event, index);
			handleItemClick(event);
		},
		[onClick, index],
	);

	return (
		<ArrowFocusTrapItemRoot
			{...props}
			{...attributes}
			as={ContainerComponent}
			ref={rootRef}
			className={clsx(classes.root, className)}
			onClick={handleClick}
			onMouseDown={handleItemInteractStart}
		>
			{childrenProp}
		</ArrowFocusTrapItemRoot>
	);
};

export const ArrowFocusTrapItem = React.forwardRef(
	ArrowFocusTrapItemPrimitive,
) as <
	C extends
		| HTMLDivElement
		| HTMLLIElement
		| React.ComponentType = HTMLDivElement,
>(
	props: ArrowFocusTrapItemProps<C>,
	ref?: ArrowFocusTrapItemRef,
) => JSX.Element;

export default ArrowFocusTrapItem;
